/**
 * HLOS-254 — Learning Experience (LX) Bridge: TS ↔ Python.
 *
 * Wrapper sobre `pyodide.runPythonAsync` que chama o helper Python
 * `hlos.lx.bridge.lx_bridge_handler` que serializa chamadas para
 * `LXEngine.start_lx / process_interaction / abandon_lx / delete_lx_state`.
 *
 * **Por que existe:**
 * v0.29.0 (HLOS-249) deixou o store Pinia com placeholder (`console.warn`).
 * Esta bridge é a **implementação real** que conecta UI → Pyodide → Python.
 *
 * **Constitucional:**
 * - CL-07 (local-first): bridge roda 100% dentro do Pyodide local; zero rede.
 * - CL-08 (Ética fechada): bridge NUNCA envia `learner_id`, `θ_lat`, memory,
 *   ou PII para fora. Logs do dev são sanitizados via `safeLogError`.
 * - CL-09 (Explicabilidade): erros estruturados rastreáveis por correlation_id.
 * - Princípio XI (Companion ≠ LLM): copy de erro nunca cita nome de provedor.
 * - CC-05 (zero penalidade): `abandonLX`/`deleteLXState` NÃO atualizam θ_lat.
 *
 * **Refs:**
 * - HLOS-254 — Pinia store: chamar Pyodide bridge real
 * - HLOS-249 — Vue renderers (a11y + bounded context)
 * - HLOS-242 — `hlos/lx/{contracts,registry,planner,engine,observation}.py`
 * - HLOS-247 — Integração LX ↔ CompanionRuntime
 * - Skill `pyodide-bridge` — patterns canônicos
 */

import type { PyodideLike } from "./bridge";
import { PyodideUnavailableError } from "./bridge";
import { ConstitutionalViolationError } from "./flows";
import type {
  LXState,
  LXInteractionEvent,
  LXInteractionResponse,
  LXType,
} from "../lx/types";

// ─────────────────────────────────────────────────────────────────────────────
// Sanitização de logs (CL-08 — HLOS-254 ressalva #2)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * HLOS-254 ressalva #2 (Constitutional Guardian): sanitize mensagens de erro
 * antes de propagar para o caller (que pode logar no console).
 *
 * Lista de keywords PII / provedor LLM (Princípio XI + CL-08):
 * - learner_id, learner_name, masterKey, theta_lat, memory_store
 * - OpenAI, Anthropic, GPT, Claude, Gemini, WebLLM, Foundation Models
 */
const FORBIDDEN_LOG_KEYWORDS = [
  // Provedores LLM (Princípio XI)
  "openai", "anthropic", "gpt-", "gpt4", "gpt-4", "gpt-3", "claude",
  "gemini", "chatgpt", "foundation", "webllm", "mlx", "qnn", "onnx",
  "apple intelligence", "apple-intelligence",
  // PII / sensíveis (CL-08)
  "masterkey", "master_key",
  "theta_lat", "θ_lat",
  "memory_store", "goal_store",
  "learner_id", "learner_name",
];

/**
 * Substitui keywords proibidas por zero-width space (preserva length mas
 * impede fingerprint). Caso a mensagem contenha PII, a substituição não é
 * reversível — caller vê mensagem higienizada.
 */
function sanitizeMessage(raw: string): string {
  let sanitized = raw;
  for (const keyword of FORBIDDEN_LOG_KEYWORDS) {
    sanitized = sanitized.replace(new RegExp(keyword, "gi"), "\u200b");
  }
  return sanitized;
}

// ─────────────────────────────────────────────────────────────────────────────
// Type guards runtime (sem `any`)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Valida shape de `LXState` retornado pelo kernel Python.
 * Schema espelha `hlos/lx/contracts.py:LXState` (8 campos canônicos).
 */
function isLXState(raw: unknown): raw is LXState {
  if (typeof raw !== "object" || raw === null) return false;
  const obj = raw as Record<string, unknown>;
  return (
    typeof obj["lx_type"] === "string" &&
    typeof obj["lx_id"] === "string" &&
    typeof obj["instance_id"] === "string" &&
    typeof obj["status"] === "string" &&
    typeof obj["content"] === "object" &&
    obj["content"] !== null &&
    typeof obj["started_at"] === "number" &&
    (obj["completed_at"] === null || typeof obj["completed_at"] === "number") &&
    typeof obj["abandoned_recently"] === "boolean"
  );
}

/**
 * Valida shape de `LXInteractionResponse`:
 *   { state: LXState, interaction: dict com campos canônicos }
 */
function isLXInteractionResponse(raw: unknown): raw is LXInteractionResponse {
  if (typeof raw !== "object" || raw === null) return false;
  const obj = raw as Record<string, unknown>;
  return isLXState(obj["state"]) && typeof obj["interaction"] === "object" && obj["interaction"] !== null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helper Python (rodado UMA VEZ no Pyodide init)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * String Python que define `hlos.lx.bridge.lx_bridge_handler` — função que
 * serializa chamadas para LXEngine canônico. Instalada uma vez via
 * `runPythonAsync` no primeiro uso do store.
 *
 * Por que string Python embarcado em TS:
 * - Self-contained (não precisa de arquivo .py separado carregado).
 * - Atomic com a chamada (mesma transação Pyodide).
 * - Re-hidrata state via `_instances: dict` interno.
 *
 * Para produção: o CompanionRuntime deve popular `_engine` via HLOS-247 wire
 * (registry + vault + learner_id canônico). Esta bridge v1 aceita trade-off:
 * cria instância ad-hoc por chamada (sem persistência cross-call), suficiente
 * para HLOS-254 v1 (testes + smoke).
 */
const PYTHON_BRIDGE_SOURCE = `
import json
import time
from hlos.lx.engine import LXEngine
from hlos.lx.observation import LXObservationBuilder
from hlos.lx.contracts import (
    LXState as _LXState,
    LXType as _LXType,
    LXStatus as _LXStatus,
)


class _BridgeVault:
    """Vault stub para HLOS-254 v1 — persiste em memória (não-cifrado).

    Para produção (HLOS-247), substituir por CompanionRuntime._vault.
    """

    def __init__(self):
        self._data: dict = {}

    def read_data(self) -> dict:
        return dict(self._data)

    def write_data(self, data: dict) -> None:
        self._data = dict(data)


# Singleton per learner_id (HLOS-254 v1: state in-memory)
_engines: dict = {}


def _get_engine(learner_id: str) -> LXEngine:
    """HLOS-254 v1: retorna LXEngine por learner (singleton)."""
    if learner_id not in _engines:
        _engines[learner_id] = LXEngine(
            vault=_BridgeVault(),
            observation_builder=LXObservationBuilder(),
            registry=None,  # HLOS-247 wire
        )
    return _engines[learner_id]


def _serialize_state(state) -> dict:
    """Serializa LXState canônico para JSON."""
    return {
        "lx_type": state.lx_type.value,
        "lx_id": state.lx_id,
        "instance_id": state.instance_id,
        "status": state.status.value,
        "content": state.content,
        "started_at": state.started_at,
        "completed_at": state.completed_at,
        "abandoned_recently": state.abandoned_recently,
    }


def lx_bridge_handler(action: str, payload: dict) -> str:
    """Dispatch canônico para LX Layer via Pyodide.

    Args:
        action: 'start_lx' | 'process_interaction' | 'abandon_lx' | 'delete_lx_state' | 'delete_all_lx_state'
        payload: dict com campos específicos por action.

    Returns:
        String JSON com resultado (sempre serializado — kernel protocol).

    Raises:
        KeyError: instance_id não existe (process/abandon/delete sem state).
        ValueError: action desconhecida ou payload inválido.
    """
    learner_id = payload.get("learner_id", "")
    if not isinstance(learner_id, str) or not learner_id:
        raise ValueError("learner_id obrigatório (CL-08)")

    if action == "start_lx":
        engine = _get_engine(learner_id)
        state = engine.start_lx(
            lx_type=_LXType(payload["lx_type"]),
            lx_id=payload["lx_id"],
            content=payload.get("content", {}),
        )
        return json.dumps(_serialize_state(state))

    if action == "process_interaction":
        engine = _get_engine(learner_id)
        new_state, interaction = engine.process_interaction(
            instance_id=payload["instance_id"],
            interaction_event=payload["event"],
        )
        return json.dumps({
            "state": _serialize_state(new_state),
            "interaction": {
                "interaction_id": interaction.interaction_id,
                "timestep": interaction.timestep,
                "ka_id": interaction.ka_id,
                "action_type": interaction.action_type,
                "correct": interaction.correct,
                "rt_ms": interaction.rt_ms,
                "confidence": interaction.confidence,
                "affect": interaction.affect,
                "curiosity_signal": interaction.curiosity_signal,
            },
        })

    if action == "abandon_lx":
        engine = _get_engine(learner_id)
        abandoned = engine.abandon_lx(instance_id=payload["instance_id"])
        return json.dumps(_serialize_state(abandoned))

    if action == "delete_lx_state":
        engine = _get_engine(learner_id)
        deleted = engine.delete_lx_state(instance_id=payload["instance_id"])
        return json.dumps({"deleted": deleted})

    if action == "delete_all_lx_state":
        engine = _get_engine(learner_id)
        count = engine.delete_all_lx_state()
        return json.dumps({"deleted_count": count})

    raise ValueError(f"action desconhecida: {action!r}")


# HLOS-254: marca que o bridge foi instalado
_bridge_installed = True
`;

/**
 * Garante que o helper Python está instalado no Pyodide runtime.
 * Idempotente — `exec()` em Python é no-op se módulo já carregado.
 */
let bridgeInstalled = false;
async function ensureBridgeInstalled(py: PyodideLike): Promise<void> {
  if (bridgeInstalled) return;
  await py.runPythonAsync(PYTHON_BRIDGE_SOURCE);
  bridgeInstalled = true;
}

// ─────────────────────────────────────────────────────────────────────────────
// Chamadas reais ao kernel Python
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Cria nova LX em status IN_PROGRESS.
 *
 * HLOS-254 corrige placeholder da v0.29.0 (HLOS-249 store.ts chamava
 * `console.warn` em vez do bridge).
 *
 * Args:
 *   py: instância Pyodide (de `initPyodide()`).
 *   lxType: tipo da LX (`question` | `quiz` | `flashcard` | `crossword`).
 *   lxId: identificador canônico da LX no registry.
 *   content: parâmetros específicos (ex.: difficulty, num_questions).
 *   learnerId: ID do learner (CL-08 sanitizado via Privacy Boundary antes).
 *
 * Returns:
 *   LXState com status=IN_PROGRESS, instance_id novo, started_at setado.
 *
 * Throws:
 *   PyodideUnavailableError: Pyodide não está pronto (produção).
 *   ConstitutionalViolationError: kernel detectou violação CC-01..08.
 *   Error: erro técnico (Pyodide runtime, payload inválido).
 */
export async function startLX(
  py: PyodideLike,
  lxType: LXType,
  lxId: string,
  content: Record<string, unknown>,
  learnerId: string,
): Promise<LXState> {
  const result = await callBridge(py, "start_lx", {
    lx_type: lxType,
    lx_id: lxId,
    content,
    learner_id: learnerId,
  }, "startLX");
  return parseLXState(result, "startLX");
}

/**
 * Processa interação do learner com a LX ativa. Alimenta o Particle Filter
 * via `LXEngine.process_interaction` → `LXObservationBuilder.from_lx`.
 */
export async function processLXInteraction(
  py: PyodideLike,
  instanceId: string,
  event: LXInteractionEvent,
  learnerId: string,
): Promise<LXInteractionResponse> {
  const result = await callBridge(py, "process_interaction", {
    instance_id: instanceId,
    event,
    learner_id: learnerId,
  }, "processLXInteraction");
  const parsed = JSON.parse(String(result)) as unknown;
  if (!isLXInteractionResponse(parsed)) {
    throw new Error(
      `[HLOS-254] processLXInteraction: payload inválido (não-LXInteractionResponse). ` +
      `Tipo: ${typeof parsed}.`,
    );
  }
  return parsed;
}

/**
 * Marca LX como ABANDONED. **CC-05 + Princípio V**: NÃO penaliza θ_lat.
 */
export async function abandonLX(
  py: PyodideLike,
  instanceId: string,
  learnerId: string,
): Promise<LXState> {
  const result = await callBridge(py, "abandon_lx", {
    instance_id: instanceId,
    learner_id: learnerId,
  }, "abandonLX");
  return parseLXState(result, "abandonLX");
}

/**
 * HLOS-254 — deleta LX state do vault cifrado (LGPD Art. 18).
 */
export async function deleteLXState(
  py: PyodideLike,
  instanceId: string,
  learnerId: string,
): Promise<{ deleted: boolean }> {
  const result = await callBridge(py, "delete_lx_state", {
    instance_id: instanceId,
    learner_id: learnerId,
  }, "deleteLXState");
  const parsed = JSON.parse(String(result)) as { deleted: boolean };
  return parsed;
}

/**
 * HLOS-254 — deleta TODAS as LXs do learner (LGPD Art. 18 — right to be forgotten).
 */
export async function deleteAllLXState(
  py: PyodideLike,
  learnerId: string,
): Promise<{ deletedCount: number }> {
  const result = await callBridge(py, "delete_all_lx_state", {
    learner_id: learnerId,
  }, "deleteAllLXState");
  const parsed = JSON.parse(String(result)) as { deleted_count: number };
  return { deletedCount: parsed.deleted_count };
}

// ─────────────────────────────────────────────────────────────────────────────
// Error handling — bridge uniforme
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Wrapper que captura `PyodideUnavailableError` (produção) e `Error` genérico
 * (dev/test) e re-lança com contexto HLOS-254.
 *
 * Args:
 *   py: Pyodide.
 *   action: nome da action Python (`start_lx`, `process_interaction`, etc.).
 *   payload: dict JSON-serializable.
 *   opName: nome da operação TS (para log estruturado).
 */
async function callBridge(
  py: PyodideLike,
  action: string,
  payload: Record<string, unknown>,
  opName: string,
): Promise<unknown> {
  await ensureBridgeInstalled(py);
  // Serializa payload como JSON string (evita problemas de marshalling PyProxy)
  const payloadJson = JSON.stringify(payload);

  try {
    return await py.runPythonAsync(
      `import json\nlx_bridge_handler(${JSON.stringify(action)}, json.loads(${JSON.stringify(payloadJson)}))`,
    );
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e);

    // Detecta violação constitucional via sentinel string
    if (message.includes("ConstitutionalViolation") || message.includes("CC-")) {
      throw new ConstitutionalViolationError([message]);
    }
    // Re-raise PyodideUnavailableError (já é o tipo correto)
    if (e instanceof PyodideUnavailableError) {
      throw e;
    }
    // Erro técnico — wrap com contexto + sanitização (HLOS-254 ressalva #2)
    const sanitized = sanitizeMessage(message);
    throw new Error(
      `[HLOS-254] ${opName}: Pyodide runPythonAsync falhou. ${sanitized}`,
      { cause: e },
    );
  }
}

/**
 * Parseia resultado JSON do kernel e valida que é LXState canônico.
 */
function parseLXState(result: unknown, opName: string): LXState {
  const parsed = JSON.parse(String(result)) as unknown;
  if (!isLXState(parsed)) {
    throw new Error(
      `[HLOS-254] ${opName}: payload inválido (não-LXState). Tipo: ${typeof parsed}.`,
    );
  }
  return parsed;
}